import React from 'react';

import { format } from 'date-fns-tz';

import { BlueProgressBadge, GreenFilledCheckBadge, RedErrorBadge } from 'src/app/components/lib/badge-v2';
import { generateRowActions, type GridColDef } from 'src/app/components/lib/table';
import { useDuplicateWorkflowCampaignMutation } from 'src/app/queries/marketing/duplicate-workflow-campaign';
import { useStopSendingMarketingCampaignMutation } from 'src/app/queries/marketing/stop-sending-campaign';
import { useUpdateMarketingCampaignStatusMutation } from 'src/app/queries/marketing/update-campaign-status';
import { Modal, useModal } from 'src/app/state/modals';
import { useNotificationActions } from 'src/app/state/notifications';

import { AutomatedCampaignStatus, CampaignStatus } from '../../campaigns/marketing-campaign-builder/builder-context';

export function useColumns() {
  const notifications = useNotificationActions();
  const confirmModal = useModal(Modal.confirm);

  // TODO: Update to use the new workflow statuses endpoints
  const { mutate: updateMarketingCampaignStatusMutator } = useUpdateMarketingCampaignStatusMutation();
  const { mutate: duplicateWorkflowCampaignMutator } = useDuplicateWorkflowCampaignMutation();
  const { mutate: stopSendingCampaignMutator } = useStopSendingMarketingCampaignMutation();

  const columns: GridColDef[] = [
    {
      cellClassName: 'data-grid--cell-semibold',
      field: 'CampaignName',
      headerName: 'Workflow name',
      width: 305,
    },
    {
      field: 'CampaignStatusId',
      headerName: 'Status',
      width: 120,
      renderCell: ({ value }) => {
        switch (value) {
          case CampaignStatus.DRAFT:
            return <BlueProgressBadge label='Draft' />;
          case AutomatedCampaignStatus.ACTIVE:
            return <GreenFilledCheckBadge label='Active' />;
          case AutomatedCampaignStatus.INACTIVE:
            return <RedErrorBadge label='Inactive' />;
          case CampaignStatus.ARCHIVED:
            return <RedErrorBadge label='Archived' />;
          default:
            return <RedErrorBadge label='Unknown' />;
        }
      },
    },
    {
      field: 'ScheduleSendForUtc',
      headerName: 'Send date',
      valueGetter: ({ row }) => {
        if (row.CampaignStatusId === CampaignStatus.DRAFT) {
          return '--';
        }
        return row.ScheduleSendForUtc
          ? format(new Date(`${String(row.ScheduleSendForUtc)}Z`), 'MM/dd/yyyy hh:mm a')
          : '--';
      },
    },
    {
      field: 'Channel',
      headerName: 'Channel',
      valueGetter: ({ row }) => {
        let value = '';
        if (row.EmailConfig?.some((config) => config.IsActive)) {
          value += 'Email / ';
        }
        if (row.SMSConfig?.some((config) => config.IsActive)) {
          value += 'SMS / ';
        }
        if (row.PushConfig?.some((config) => config.IsActive)) {
          value += 'Push / ';
        }
        return value.slice(0, -3);
      },
    },
    {
      field: 'LastModifiedDateUTC',
      headerName: 'Last modified',
      valueGetter: ({ row }) =>
        row.LastModifiedDateUTC ? format(new Date(`${String(row.LastModifiedDateUTC)}Z`), 'MM/dd/yyyy hh:mm a') : '--',
    },
    {
      field: 'RecipientSegments',
      headerName: 'Segments',
      valueGetter: ({ row }) =>
        row.RecipientSegments?.length > 0 && Array.isArray(row.RecipientSegments)
          ? row.RecipientSegments.map((segment) => segment.Name).join(', ')
          : '--',
    },
    {
      field: 'Tags',
      headerName: 'Tags',
      valueGetter: ({ row }) =>
        row.Tags?.length > 0 && Array.isArray(row.Tags) ? row.Tags.map((tag) => tag.TagName).join(', ') : '--',
    },
    generateRowActions([
      {
        // you can't archive a campaign that is currently active
        visible: (row) => row.CampaignStatusId !== AutomatedCampaignStatus.ACTIVE,
        value: 'Archive',
        onClick: ({ MarketingCampaignId }) => {
          confirmModal.openModal({
            onConfirm: async () => {
              updateMarketingCampaignStatusMutator(
                {
                  CampaignStatusId: CampaignStatus.ARCHIVED,
                  MarketingCampaignId,
                },
                {
                  onSuccess: () => notifications.success({ message: 'Campaign archived' }),
                  onError: () => notifications.error({ message: 'Error archiving campaign' }),
                }
              );
            },
          });
        },
      },
      // New action for deactivating an active automated campaign
      {
        value: 'Deactivate',
        visible: (row) => row.CampaignStatusId === AutomatedCampaignStatus.ACTIVE,
        onClick: ({ MarketingCampaignId }) => {
          confirmModal.openModal({
            title: 'Deactivate workflow',
            helpText:
              'This will stop the workflow from being sent to customers and will cancel in-progress workflow runs. Are you sure you want to continue?',
            onConfirm: async () => {
              stopSendingCampaignMutator(
                {
                  campaignId: MarketingCampaignId,
                },
                {
                  onSuccess: () => notifications.success({ message: 'Workflow deactivated' }),
                  onError: () => notifications.error({ message: 'Error deactivating workflow' }),
                }
              );
            },
          });
        },
      },
      {
        value: 'Revert to draft',
        visible: (row) => row.CampaignStatusId === AutomatedCampaignStatus.INACTIVE,
        onClick: ({ MarketingCampaignId }) => {
          confirmModal.openModal({
            title: 'Revert to draft',
            helpText: 'This will allow you to edit the workflow. Are you sure you want to continue?',
            onConfirm: async () => {
              updateMarketingCampaignStatusMutator(
                {
                  CampaignStatusId: CampaignStatus.DRAFT,
                  MarketingCampaignId,
                },
                {
                  onSuccess: () => notifications.success({ message: 'Workflow reverted to draft' }),
                  onError: () => notifications.error({ message: 'Error reverting workflow to draft' }),
                }
              );
            },
          });
        },
      },
      {
        value: 'Duplicate',
        visible: true,
        onClick: (row) => {
          duplicateWorkflowCampaignMutator(
            { marketingCampaignId: row.MarketingCampaignId },
            {
              onSuccess: () => notifications.success({ message: 'Workflow copied to new draft' }),
              onError: () => notifications.error({ message: 'Error copying workflow' }),
            }
          );
        },
      },
    ]),
  ];

  return columns;
}
